import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";

export interface EnvironmentView { key: string; name: string; nameAr?: string | null; order: number; baseUrl: string; isCurrent: boolean; trustKeyRef?: string | null; color: string; enabled: boolean }
export interface EnvironmentsView { environments: EnvironmentView[]; current: string; next?: string | null; previous?: string | null }
export interface EnvironmentInput { name?: string; nameAr?: string | null; baseUrl?: string; order?: number; trustKeyRef?: string; color?: string; enabled?: boolean }

export type PromotionStatus = "Sent" | "Received" | "Failed" | "Published" | "RolledBack" | "Rejected";
export interface PromotedItem { type: string; key: string; version?: number | null; name: string }
export interface PromotionView {
  id: string; direction: "Outbound" | "Inbound"; source: string; target: string; changeSetId?: string | null; sourceChangeSetId?: string | null; title: string;
  status: PromotionStatus; localReleaseStatus?: string | null; items?: PromotedItem[] | null; packageSha256: string; requestedByName?: string | null; error?: string | null;
  remotePromotionId?: string | null; evaluations: number; createdAt: string; updatedAt: string;
}
export interface MatrixEnvironment { key: string; name: string; nameAr?: string | null; color: string; isCurrent: boolean; reachable: boolean; error?: string | null }
export interface MatrixRow { type: string; key: string; name: string; versions: Record<string, number | null>; present?: Record<string, boolean>; hashes?: Record<string, string | null> }
export interface PromotionMatrix { environments: MatrixEnvironment[]; rows: MatrixRow[] }

const K = ["platform", "promotions"] as const;

export function useEnvironments(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: [...K, "environments"], queryFn: () => api.get<EnvironmentsView>("/api/platform/environments"), staleTime: 30_000, enabled });
}

export function usePromotions(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: [...K, "history"], queryFn: () => api.get<PromotionView[]>("/api/platform/promotions"), staleTime: 5_000, enabled });
}

export function usePromotionMatrix() {
  const api = useApi();
  return useQuery({ queryKey: [...K, "matrix"], queryFn: () => api.get<PromotionMatrix>("/api/platform/promotions/matrix"), staleTime: 15_000, retry: false });
}

export function usePromotionActions() {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const done = () => { void qc.invalidateQueries({ queryKey: K }); void qc.invalidateQueries({ queryKey: ["change-sets"] }); void qc.invalidateQueries({ queryKey: ["change-set"] }); };
  // Each reports its own failure with the cause; sending, asking the target and rolling back are not a "Not saved".
  return {
    promote: useSave({ mutationFn: (changeSetId: string) => api.post<PromotionView>("/api/platform/promotions", { changeSetId }), failed: t("admin:common.error"), onSuccess: done }),
    refresh: useSave({ mutationFn: (id: string) => api.post<PromotionView>(`/api/platform/promotions/${id}/refresh`), failed: t("admin:common.error"), onSuccess: done }),
    rollback: useSave({ mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post<{ rollbackChangeSetId: string }>(`/api/platform/promotions/${id}/rollback`, { reason }), failed: t("admin:common.error"), onSuccess: done }),
    save: useSave({ mutationFn: ({ key, input }: { key: string; input: EnvironmentInput }) => api.put<EnvironmentView>(`/api/platform/environments/${key}`, input), onSuccess: done }),
  };
}
